import Phaser from 'phaser';
import { sounds } from '../audio/SoundEffects';
import { progression, PERKS, MISSIONS } from '../game/Progression';

export class ShopModal {
  constructor(scene, onCloseCallback) {
    this.scene = scene;
    this.onClose = onCloseCallback;
    this.create();
  }

  create() {
    const { width, height } = this.scene.scale;

    this.container = this.scene.add.container(0, 0).setDepth(200);

    // Dimmed background backdrop
    const backdrop = this.scene.add.rectangle(width / 2, height / 2, width, height, 0x020617, 0.88);
    backdrop.setInteractive(); // Block clicks through to scene below

    // Modal Panel
    const panelW = Math.min(width - 32, 420);
    const panelH = Math.min(height - 80, 720);
    const panelX = width / 2;
    const panelY = height / 2;

    const panelBg = this.scene.add.graphics();
    panelBg.fillStyle(0x0f172a, 0.98);
    panelBg.fillRoundedRect(panelX - panelW / 2, panelY - panelH / 2, panelW, panelH, 20);
    panelBg.lineStyle(3, 0xf59e0b, 1);
    panelBg.strokeRoundedRect(panelX - panelW / 2, panelY - panelH / 2, panelW, panelH, 20);

    // Header Title
    const title = this.scene.add.text(panelX, panelY - panelH / 2 + 35, '⚡ ROCKSTAR PERK SHOP ⚡', {
      fontFamily: 'Impact, Arial Black, sans-serif',
      fontSize: '24px',
      color: '#facc15',
      stroke: '#b45309',
      strokeThickness: 4
    }).setOrigin(0.5);

    // Bank Balance Display
    this.bankText = this.scene.add.text(panelX, panelY - panelH / 2 + 70, `📀 BANK: ${progression.getBankVinyls()} VINYLS`, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '17px',
      color: '#fde047',
      fontStyle: 'bold',
      backgroundColor: '#1e293b',
      padding: { x: 14, y: 6 }
    }).setOrigin(0.5);

    // Close Button [X] in top-right
    const closeBtn = this.scene.add.text(panelX + panelW / 2 - 25, panelY - panelH / 2 + 25, '✕', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '26px',
      color: '#94a3b8',
      fontStyle: 'bold'
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    closeBtn.on('pointerdown', () => this.destroy());

    this.container.add([backdrop, panelBg, title, this.bankText, closeBtn]);

    // Perk cards listing
    let startY = panelY - panelH / 2 + 115;
    const perksList = Object.values(PERKS);

    perksList.forEach((perk) => {
      const card = this.createPerkCard(panelX, startY, panelW - 40, perk);
      this.container.add(card);
      startY += 78;
    });

    // Missions Header
    const missionHeader = this.scene.add.text(panelX, startY + 10, '🎯 DAILY ROCKSTAR MISSIONS', {
      fontFamily: 'Impact, Arial Black, sans-serif',
      fontSize: '18px',
      color: '#38bdf8'
    }).setOrigin(0.5);
    this.container.add(missionHeader);
    startY += 32;

    // Missions list
    MISSIONS.forEach((mission) => {
      const mCard = this.createMissionCard(panelX, startY, panelW - 40, mission);
      this.container.add(mCard);
      startY += 62;
    });

    // Close button at bottom
    const doneBtn = this.scene.add.container(panelX, panelY + panelH / 2 - 40);
    const dBg = this.scene.add.graphics();
    dBg.fillStyle(0x3b82f6, 1);
    dBg.fillRoundedRect(-90, -22, 180, 44, 22);
    dBg.lineStyle(2, 0x93c5fd, 1);
    dBg.strokeRoundedRect(-90, -22, 180, 44, 22);

    const dTxt = this.scene.add.text(0, 0, 'BACK TO GAME', {
      fontFamily: 'Impact, Arial Black, sans-serif',
      fontSize: '18px',
      color: '#ffffff'
    }).setOrigin(0.5);

    doneBtn.add([dBg, dTxt]);
    doneBtn.setInteractive(new Phaser.Geom.Rectangle(-90, -22, 180, 44), Phaser.Geom.Rectangle.Contains);
    doneBtn.on('pointerdown', () => this.destroy());
    this.container.add(doneBtn);

    // Zoom-in pop animation
    this.container.setScale(0.85);
    this.container.setAlpha(0);
    this.scene.tweens.add({
      targets: this.container,
      scaleX: 1,
      scaleY: 1,
      alpha: 1,
      duration: 220,
      ease: 'Back.easeOut'
    });
  }

  createPerkCard(x, y, w, perk) {
    const card = this.scene.add.container(x, y);

    const isOwned = progression.hasPerk(perk.id);

    const bg = this.scene.add.graphics();
    bg.fillStyle(0x1e293b, 0.9);
    bg.fillRoundedRect(-w / 2, -34, w, 68, 12);
    bg.lineStyle(2, isOwned ? 0x22c55e : 0x475569, 0.8);
    bg.strokeRoundedRect(-w / 2, -34, w, 68, 12);

    const icon = this.scene.add.text(-w / 2 + 25, 0, perk.icon, {
      fontSize: '26px'
    }).setOrigin(0.5);

    const name = this.scene.add.text(-w / 2 + 50, -18, perk.name, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '14px',
      color: '#f8fafc',
      fontStyle: 'bold'
    });

    const desc = this.scene.add.text(-w / 2 + 50, 4, perk.desc, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '10.5px',
      color: '#94a3b8',
      wordWrap: { width: w - 165 }
    });

    // Buy / Owned button
    const btnX = w / 2 - 50;
    const btn = this.scene.add.container(btnX, 0);

    const bBg = this.scene.add.graphics();
    bBg.fillStyle(isOwned ? 0x166534 : 0xf59e0b, 1);
    bBg.fillRoundedRect(-42, -18, 84, 36, 18);
    bBg.lineStyle(2, isOwned ? 0x4ade80 : 0xfef08a, 1);
    bBg.strokeRoundedRect(-42, -18, 84, 36, 18);

    const bTxt = this.scene.add.text(0, 0, isOwned ? 'EQUIPPED' : `📀 ${perk.price}`, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '11.5px',
      color: isOwned ? '#bbf7d0' : '#0f172a',
      fontStyle: 'bold'
    }).setOrigin(0.5);

    btn.add([bBg, bTxt]);

    if (!isOwned) {
      btn.setInteractive(new Phaser.Geom.Rectangle(-42, -18, 84, 36), Phaser.Geom.Rectangle.Contains);
      btn.on('pointerdown', () => {
        if (progression.buyPerk(perk.id)) {
          sounds.playRockChord();
          this.refresh();
        } else {
          sounds.playCrash();
          this.scene.cameras.main.shake(120, 0.008);
        }
      });
    }

    card.add([bg, icon, name, desc, btn]);
    return card;
  }

  createMissionCard(x, y, w, mission) {
    const card = this.scene.add.container(x, y);
    const isCompleted = progression.isMissionCompleted(mission.id);

    const bg = this.scene.add.graphics();
    bg.fillStyle(0x1e293b, 0.85);
    bg.fillRoundedRect(-w / 2, -26, w, 52, 10);
    bg.lineStyle(1.5, isCompleted ? 0x22c55e : 0x0284c7, 0.7);
    bg.strokeRoundedRect(-w / 2, -26, w, 52, 10);

    const icon = this.scene.add.text(-w / 2 + 22, 0, mission.icon, {
      fontSize: '20px'
    }).setOrigin(0.5);

    const title = this.scene.add.text(-w / 2 + 45, -13, mission.title, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '12.5px',
      color: '#ffffff',
      fontStyle: 'bold'
    });

    const desc = this.scene.add.text(-w / 2 + 45, 4, mission.desc, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '10px',
      color: '#94a3b8'
    });

    const statusBadge = this.scene.add.text(w / 2 - 45, 0, isCompleted ? 'DONE (+📀)' : `+${mission.reward} 📀`, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '11px',
      color: isCompleted ? '#4ade80' : '#fde047',
      fontStyle: 'bold',
      backgroundColor: '#0f172a',
      padding: { x: 6, y: 3 }
    }).setOrigin(0.5);

    card.add([bg, icon, title, desc, statusBadge]);
    return card;
  }

  refresh() {
    this.destroy();
    this.create();
  }

  destroy() {
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
    if (this.onClose) {
      this.onClose();
    }
  }
}
